import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmWatersService } from '@services/crm-waters.service';
import { BreadcrumbComponent, type BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';
import { SeasonPeriodsComponent } from './season-periods/season-periods.component';

/** Index of the "Pricing" tab on the water form — used to return there via the breadcrumb. */
const PRICING_TAB_INDEX = 5;

/**
 * Routed pricing-details page for a water. Currently hosts the "Season periods" section; the later
 * WaterPrice work will attach additional sections here.
 */
@Component({
  selector: 'app-water-pricing',
  templateUrl: './water-pricing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule, TranslatePipe, BreadcrumbComponent, SeasonPeriodsComponent],
})
export class WaterPricingComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly watersService = inject(CrmWatersService);
  private readonly translate = inject(TranslateService);

  readonly waterId = signal(0);

  private readonly waterResource = rxResource({
    params: () => (this.waterId() ? { waterId: this.waterId() } : undefined),
    stream: ({ params }) => this.watersService.getWater(params.waterId),
  });

  private readonly waterTitle = computed(() => this.waterResource.value()?.title ?? '');

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('nav.waters'), routerLink: ['/waters'] },
    {
      label: this.waterTitle() || String(this.waterId()),
      routerLink: ['/waters/edit', this.waterId()],
      queryParams: { tab: PRICING_TAB_INDEX },
    },
    { label: this.translate.instant('waters.pricing.breadcrumb') },
  ]);

  ngOnInit(): void {
    this.waterId.set(Number(this.route.snapshot.paramMap.get('id')));
  }
}
